import { Lock } from "lucide-react";
import type { SecurityDepositMode } from "@fm/api";
import { CREDIT_CARD_NOTICE, holdRenewalNoticeFor } from "./depositCopy";

export interface SecurityDepositBannerProps {
  amount: number;
  currency: string;
  mode: SecurityDepositMode;
  acceptCash?: boolean;
  /** True when the deposit hold needs a credit card (no debit/prepaid). */
  creditCardRequired?: boolean;
  className?: string;
}

const formatAmount = (amount: number, currency: string) =>
  `$${amount.toFixed(2)} ${currency}`;

const titleFor = (mode: SecurityDepositMode): string => {
  switch (mode) {
    case "after_checkout":
      return "Security Deposit — Authorize After Booking";
    case "at_checkin":
      return "Security Deposit — Authorized Automatically";
    case "manual":
      return "Security Deposit — Processed Separately";
    default:
      return "Security Deposit";
  }
};

const explanationFor = (
  mode: SecurityDepositMode,
  amount: number,
  currency: string,
  acceptCash?: boolean,
): string => {
  const formatted = formatAmount(amount, currency);

  switch (mode) {
    case "after_checkout":
      return `After booking, you'll receive an email with a secure link to authorize a ${formatted} hold on your card. This is not a charge — funds are released after your rental ends, assuming no damages or fees apply.`;

    case "at_checkin":
      return `A ${formatted} hold will be placed automatically on your card at the start of your pickup date${
        acceptCash ? " (cash payment may also be accepted)" : ""
      }. This is not a charge — funds are released after your rental ends.`;

    case "manual":
      return `A ${formatted} security deposit will be requested separately by the host. It is not collected during this checkout.`;

    default:
      return `Security deposit: ${formatted}.`;
  }
};

export function SecurityDepositBanner({
  amount,
  currency,
  mode,
  acceptCash,
  creditCardRequired = false,
  className = "",
}: SecurityDepositBannerProps) {
  if (mode === "disabled") return null;

  const renewalNotice = holdRenewalNoticeFor(mode);

  return (
    <div
      className={`bg-blue-50 border border-blue-200 rounded-lg p-4 ${className}`}
    >
      <div className="flex items-start gap-3">
        <Lock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="font-semibold text-blue-900 mb-1">{titleFor(mode)}</h4>
          <p className="text-sm text-blue-800">
            {explanationFor(mode, amount, currency, acceptCash)}
          </p>
          {renewalNotice && (
            <p
              className="text-sm text-blue-800 mt-1"
              data-testid="deposit-hold-renewal-notice"
            >
              {renewalNotice}
            </p>
          )}
          {creditCardRequired && (
            <p
              className="text-sm font-medium text-blue-900 mt-2"
              data-testid="deposit-credit-card-notice"
            >
              {CREDIT_CARD_NOTICE}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
